import type { WebContainer, WebContainerProcess } from '@webcontainer/api';
import { atom, type WritableAtom } from 'nanostores';
import type { ITerminal } from '~/types/terminal';
import { newDualiteShellProcess, newShellProcess } from '~/utils/shell';
import { coloredText } from '~/utils/terminal';
import * as Sentry from '@sentry/remix';
import { userStore } from '~/lib/stores/user';

export class TerminalStore {
  #webcontainer: Promise<WebContainer>;
  #terminals: Array<{ terminal: ITerminal; process: WebContainerProcess }> = [];
  #dualiteTerminal = newDualiteShellProcess();

  showTerminal: WritableAtom<boolean> = import.meta.hot?.data.showTerminal ?? atom(false);

  constructor(webcontainerPromise: Promise<WebContainer>) {
    this.#webcontainer = webcontainerPromise;

    if (import.meta.hot) {
      import.meta.hot.data.showTerminal = this.showTerminal;
    }
  }

  get dualiteTerminal() {
    return this.#dualiteTerminal;
  }

  async attachDualiteTerminal(terminal: ITerminal) {
    try {
      const wc = await this.#webcontainer;
      await this.#dualiteTerminal.init(wc, terminal);
      this.toggleTerminal(true);
    } catch (error: any) {
      /*
       * Sentry.captureException(error, {
       *   extra: { message: 'Failed to spawn dualite shell', terminalId: terminal },
       *   level: 'log',
       * });
       */
      terminal.write(coloredText.red('Failed to spawn alpha shell\n\n') + error.message);
      return;
    }
  }

  toggleTerminal(value?: boolean) {
    this.showTerminal.set(value !== undefined ? value : !this.showTerminal.get());
  }

  async attachTerminal(terminal: ITerminal) {
    try {
      const shellProcess = await newShellProcess(await this.#webcontainer, terminal);
      this.#terminals.push({ terminal, process: shellProcess });
    } catch (error: any) {
      /*
       * Sentry.captureException(error, {
       *   extra: { message: 'Failed to spawn shell', terminalId: terminal },
       *   level: 'log',
       * });
       */
      terminal.write(coloredText.red('Failed to spawn shell\n\n') + error.message);
      return;
    }
  }

  onTerminalResize(cols: number, rows: number) {
    for (const { process } of this.#terminals) {
      process.resize({ cols, rows });
    }
  }
}
